import { FC, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircle2, Loader2, Crown, ArrowRight } from 'lucide-react'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'
import Page from '../../components/page/page'
import { useUserContext } from '../../hooks/use-user'
import { useAuth } from '../../hooks/use-firebase'

export const SubscriptionSuccess: FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { subscription } = useUserContext()
  const { currentUser } = useAuth()
  const [isRefreshing, setIsRefreshing] = useState(true)

  // Force token refresh to get updated custom claims
  useEffect(() => {
    const refreshToken = async () => {
      if (currentUser) {
        try {
          // Force refresh the ID token to get updated custom claims
          await currentUser.getIdToken(true)

          // Wait a moment for the context to update
          setTimeout(() => {
            setIsRefreshing(false)
          }, 2000)
        } catch (error) {
          console.error('Error refreshing token:', error)
          setIsRefreshing(false)
        }
      } else {
        setIsRefreshing(false)
      }
    }

    refreshToken()
  }, [currentUser])

  const handleGoToDashboard = () => {
    navigate('/')
  }

  const handleViewSettings = () => {
    navigate('/settings')
  }

  return (
    <Page title="Subscription Successful" hideNavbar={false}>
      <div className="flex items-center justify-center min-h-[60vh] p-6">
        <Card className="max-w-2xl w-full border-2 border-success500">
          <CardHeader className="text-center pb-4">
            <div className="flex justify-center mb-4">
              {isRefreshing ? (
                <div className="w-20 h-20 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
                  <Loader2 className="w-10 h-10 text-sumoPrimary animate-spin" />
                </div>
              ) : (
                <div className="w-20 h-20 rounded-full bg-success500/10 flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-success500" />
                </div>
              )}
            </div>
            <CardTitle className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">
              {isRefreshing ? 'Activating Your Subscription...' : `Welcome to ${subscription.charAt(0).toUpperCase() + subscription.slice(1)}!`}
            </CardTitle>
            <CardDescription className="text-lg mt-2">
              {isRefreshing
                ? 'Please wait while we activate your subscription'
                : 'Your subscription has been activated successfully'
              }
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {!isRefreshing && (
              <>
                {/* Success Message */}
                <div className="bg-success500/10 border border-success500/20 rounded-lg p-4 text-center">
                  <p className="text-neutral-700 dark:text-neutral-300">
                    Thank you for subscribing! You now have access to all {subscription.charAt(0).toUpperCase() + subscription.slice(1)} features.
                  </p>
                </div>

                {/* Current Plan Badge */}
                <div className="flex items-center justify-center gap-2 py-4">
                  <Crown className="w-5 h-5 text-sumoPrimary" />
                  <span className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                    Current Plan: <span className="text-sumoPrimary capitalize">{subscription}</span>
                  </span>
                </div>

                {/* What's Next */}
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                    What's Next?
                  </h3>
                  <ul className="space-y-2 text-sm text-neutral-700 dark:text-neutral-300">
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-sumoPrimary mt-2" />
                      <span>Access your <strong>Listings Dashboard</strong> to start creating product listings</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-sumoPrimary mt-2" />
                      <span>Track sales and view <strong>earnings analytics</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-sumoPrimary mt-2" />
                      <span>Generate and print <strong>barcodes</strong> for your inventory</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-sumoPrimary mt-2" />
                      <span>Manage your subscription in <strong>Settings</strong></span>
                    </li>
                  </ul>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Button
                    onClick={handleGoToDashboard}
                    variant="primary"
                    size="lg"
                    className="flex-1"
                  >
                    Go to Dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button
                    onClick={handleViewSettings}
                    variant="outline"
                    size="lg"
                    className="flex-1"
                  >
                    View Settings
                  </Button>
                </div>

                {/* Receipt Info */}
                <div className="text-center text-sm text-neutral-500 dark:text-neutral-400 pt-4 border-t dark:border-neutral-700">
                  <p>A receipt has been sent to your email.</p>
                  <p className="mt-1">You can manage your subscription anytime in Settings.</p>
                </div>
              </>
            )}

            {isRefreshing && (
              <div className="py-8 text-center">
                <p className="text-neutral-600 dark:text-neutral-400">
                  This may take a few moments...
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Page>
  )
}

export default SubscriptionSuccess
